/**
 * Localized rendering of the NL-014 calculation explanation.
 *
 * `buildCalculationExplanation` deliberately emits stable ids plus allowlisted
 * primitives - `"explanation.seasonal.scenario"` with `{ scenario: "lower",
 * percent: -10, multiplier: "0.90" }` - rather than sentences, so that no
 * planner free text can reach it. Something still has to turn those ids and
 * machine tokens into a sentence a visitor can read, in their own language.
 * That is this module, and it is the only place that mapping exists.
 *
 * Two rules keep it honest:
 *
 * 1. **Every id maps to a copy key.** `EXPLANATION_ENTRY_COPY` is typed as a
 *    total record over `CalculationExplanationEntryId`, so a new explanation
 *    entry fails to compile until its copy exists.
 * 2. **Every interpolated machine token is itself localized.** Component names,
 *    component statuses, blockers, package reasons, missing-information tokens
 *    and scenario names each resolve through their own key before they are
 *    substituted, so a visitor never sees `pricing-snapshot-version` or
 *    `not-used` rendered raw.
 *
 * Pure: no clock, no I/O, no state mutation, and no dependency on the content
 * entry type - callers pass a resolver so this stays testable in isolation.
 */
import type {
  CalculationExplanationEntry,
  CalculationExplanationEntryId,
} from "@/lib/funnel-planner/calculation-explanation";
import type {
  FunnelCalculationComponentName,
  FunnelCalculationComponentStatus,
} from "@/lib/funnel-planner/calculation-receipt";
import type { SeasonalPlanningScenario } from "@/lib/funnel-planner/schema";

/** `(key, fallback) => copy`. In the app this is `funnelText` bound to content. */
export type ExplanationCopyResolver = (key: string, fallback: string) => string;

type InterpolationValues = Record<string, string | number>;

/** Which value names on an entry are machine tokens needing their own lookup. */
type TokenResolvers = Readonly<Record<string, (token: string) => { key: string; fallback: string }>>;

export const SEASONAL_SCENARIO_LABEL_KEYS: Readonly<Record<SeasonalPlanningScenario, string>> = {
  lower: "seasonalScenarioLowerLabel",
  expected: "seasonalScenarioExpectedLabel",
  upper: "seasonalScenarioUpperLabel",
};

export const SEASONAL_SCENARIO_LABEL_FALLBACKS: Readonly<Record<SeasonalPlanningScenario, string>> = {
  lower: "Slow month",
  expected: "Normal month",
  upper: "Strong month",
};

const COMPONENT_LABEL_FALLBACKS: Readonly<Record<FunnelCalculationComponentName, string>> = {
  seasonalDemand: "Seasonal demand",
  priceEvidence: "Price evidence",
  packageRecommendation: "Plan suggestion",
};

const COMPONENT_STATUS_FALLBACKS: Readonly<Record<FunnelCalculationComponentStatus, string>> = {
  used: "used in this estimate",
  "not-used": "not used in this estimate",
};

/** The tokens `PackageRecommendationMissingInformation` can carry. */
const MISSING_INFORMATION_FALLBACKS: Readonly<Record<string, string>> = {
  market: "region",
  currency: "currency",
  "pricing-snapshot": "indicative pricing",
  "pricing-snapshot-version": "pricing snapshot version",
  "pricing-band": "indicative range",
};

const UNKNOWN_COMPONENT_FALLBACK = "A calculation input";
const UNKNOWN_COMPONENT_STATUS_FALLBACK = "included in this estimate";
const UNKNOWN_MISSING_INFORMATION_FALLBACK = "additional planning information";
const UNKNOWN_SCENARIO_FALLBACK = "Planning scenario";
const UNKNOWN_DETAIL_KEY = "calculationExplanationUnavailableDetail";
const UNKNOWN_DETAIL_FALLBACK = "Additional detail is unavailable.";

export function explanationComponentCopyKey(component: string): string {
  return `explanationComponent.${component}`;
}

export function explanationComponentStatusCopyKey(status: string): string {
  return `explanationComponentStatus.${status}`;
}

export function explanationMissingCopyKey(missing: string): string {
  return `explanationMissing.${missing}`;
}

export function explanationBlockerCopyKey(blocker: string): string {
  return `explanationBlocker.${blocker}`;
}

export function packageReasonCopyKey(reason: string): string {
  return `packageAdviserReason.${reason}`;
}

export function seasonalScenarioCopyKey(scenario: string): string {
  return SEASONAL_SCENARIO_LABEL_KEYS[scenario as SeasonalPlanningScenario] ?? scenario;
}

const componentToken = (token: string) => ({
  key: explanationComponentCopyKey(token),
  fallback: COMPONENT_LABEL_FALLBACKS[token as FunnelCalculationComponentName] ?? UNKNOWN_COMPONENT_FALLBACK,
});

const componentStatusToken = (token: string) => ({
  key: explanationComponentStatusCopyKey(token),
  fallback:
    COMPONENT_STATUS_FALLBACKS[token as FunnelCalculationComponentStatus] ??
    UNKNOWN_COMPONENT_STATUS_FALLBACK,
});

const missingToken = (token: string) => ({
  key: explanationMissingCopyKey(token),
  fallback: MISSING_INFORMATION_FALLBACKS[token] ?? UNKNOWN_MISSING_INFORMATION_FALLBACK,
});

const scenarioToken = (token: string) => ({
  key: seasonalScenarioCopyKey(token),
  fallback: SEASONAL_SCENARIO_LABEL_FALLBACKS[token as SeasonalPlanningScenario] ?? UNKNOWN_SCENARIO_FALLBACK,
});

type EntryCopy = {
  /**
   * A fixed content key, or a function deriving one from the entry's own token
   * when the localized sentence *is* the token (a blocker, a package reason).
   */
  readonly key: string | ((values: InterpolationValues) => string);
  readonly fallback: string;
  readonly tokens?: TokenResolvers;
};

/**
 * Total over every explanation id. Adding an id without copy is a type error.
 */
export const EXPLANATION_ENTRY_COPY: Readonly<Record<CalculationExplanationEntryId, EntryCopy>> = {
  "explanation.method.classification": {
    key: "calculationExplanationMethodVersion",
    fallback: "This estimate uses the current planner settings.",
  },
  "explanation.method.receipt": {
    key: "calculationExplanationReceiptRow",
    fallback: "The calculation uses the information entered in this session and standard rounding.",
  },
  "explanation.method.component": {
    key: "calculationExplanationComponentRow",
    fallback: "{component}: {status}.",
    tokens: { component: componentToken, status: componentStatusToken },
  },
  "explanation.package.rule": {
    key: "calculationExplanationRuleVersion",
    fallback: "The suggested plan is {recommendedTier}.",
  },
  "explanation.package.reason": {
    key: (values) => packageReasonCopyKey(String(values.reason ?? "")),
    fallback: UNKNOWN_DETAIL_FALLBACK,
  },
  "explanation.package.band": {
    key: "calculationExplanationBandRow",
    fallback: "Indicative range {pricingBand}.",
  },
  "explanation.package.pricingBlocked": {
    key: (values) => explanationBlockerCopyKey(String(values.blocker ?? "")),
    fallback: "{blocker}",
  },
  "explanation.package.missing": {
    key: "calculationExplanationMissingRow",
    fallback: "Still missing: {missing}.",
    tokens: { missing: missingToken },
  },
  "explanation.seasonal.method": {
    key: "calculationExplanationSeasonalMethod",
    fallback: "Your seasonal assumptions are applied to estimated monthly visitors.",
  },
  "explanation.seasonal.scenario": {
    key: "calculationExplanationSeasonalScenarioRow",
    fallback: "{scenario}: {percent}% change in planned visitors.",
    tokens: { scenario: scenarioToken },
  },
  "explanation.seasonal.blocked": {
    key: (values) => explanationBlockerCopyKey(String(values.blocker ?? "")),
    fallback: "{blocker}",
  },
  "explanation.range.scenario": {
    key: "calculationExplanationRangeRow",
    fallback: "{scenario}: {monthlyVisitors} monthly visitors",
    tokens: { scenario: scenarioToken },
  },
  "explanation.blocker": {
    key: (values) => explanationBlockerCopyKey(String(values.blocker ?? "")),
    fallback: "{blocker}",
  },
};

/** The fixed content keys this module can request, for coverage tests. */
export const EXPLANATION_STATIC_COPY_KEYS: readonly string[] = Object.freeze(
  Array.from(
    new Set(
      Object.values(EXPLANATION_ENTRY_COPY)
        .map((copy) => copy.key)
        .filter((key): key is string => typeof key === "string"),
    ),
  ),
);

function interpolate(source: string, values: InterpolationValues): string {
  return source.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = values[name];
    return value === undefined || value === null ? match : String(value);
  });
}

/**
 * Renders one explanation entry as localized text.
 *
 * Machine tokens are localized *before* they are substituted. Unknown tokens
 * use a generic visitor-facing message rather than exposing a raw value.
 */
export function resolveExplanationEntryText(
  entry: CalculationExplanationEntry,
  resolve: ExplanationCopyResolver,
): string {
  const copy = EXPLANATION_ENTRY_COPY[entry.id];
  if (!copy) {
    return resolve(UNKNOWN_DETAIL_KEY, UNKNOWN_DETAIL_FALLBACK);
  }
  const rawValues: InterpolationValues = Object.fromEntries(
    Object.entries(entry.values ?? {}).map(([name, value]) => [name, String(value)]),
  );

  const values: InterpolationValues = { ...rawValues };
  for (const [name, toToken] of Object.entries(copy.tokens ?? {})) {
    const token = rawValues[name];
    if (typeof token !== "string" || token.length === 0) continue;
    const { key, fallback } = toToken(token);
    values[name] = resolve(key, fallback);
  }

  const key = typeof copy.key === "string" ? copy.key : copy.key(rawValues);
  const resolved = resolve(key, copy.fallback);

  if (resolved === copy.fallback && typeof copy.key === "function") {
    return resolve(UNKNOWN_DETAIL_KEY, UNKNOWN_DETAIL_FALLBACK);
  }

  return interpolate(resolved, values);
}
